import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from '@/components/ui/toaster'
import { sendEvent, type EngineResult } from '@/lib/api'
import { Play, Trash2, ChevronDown, ChevronUp, Zap } from 'lucide-react'
import { cn } from '@/lib/utils'

const QUICK_EVENTS = {
  noise: [
    { type: 'deploy', text: 'Deploy succeeded v1.2.3' },
    { type: 'metric', text: 'CPU 62% steady' },
    { type: 'feedback', text: 'User signup: free plan' },
    { type: 'metric', text: 'Cache hit rate: 94%' },
  ],
  potential: [
    { type: 'error', text: 'Error: timeout contacting DB' },
    { type: 'error', text: 'Sentry: warning spike in checkout service' },
    { type: 'feedback', text: "User feedback: 'confusing UI'" },
    { type: 'payment', text: 'Stripe: payment failed $49 (trial user)' },
  ],
  trigger: [
    { type: 'payment', text: 'Stripe: payment failed $499 (ACME, VIP)' },
    { type: 'error', text: 'Sentry: DB connection refused (x5 in 2m)' },
    { type: 'security', text: 'Security: login from new country + new device' },
    { type: 'feedback', text: "User: 'cancel + refund now' (VIP)" },
  ],
}

const SCENARIOS = {
  calm: [
    { type: 'deploy', text: 'Deploy succeeded v1.2.3' },
    { type: 'metric', text: 'CPU 62% steady' },
    { type: 'feedback', text: 'User signup: free plan' },
    { type: 'metric', text: 'Cache hit rate 94%' },
    { type: 'error', text: 'Error: timeout contacting DB' },
    { type: 'error', text: 'Error: timeout contacting DB' },
    { type: 'payment', text: 'Stripe: payment failed $49 (trial user)' },
    { type: 'error', text: 'Sentry warning spike in checkout' },
    { type: 'deploy', text: 'Deploy succeeded v1.2.4' },
    { type: 'metric', text: 'CPU 58% steady' },
  ],
  incident: [
    { type: 'deploy', text: 'Deploy succeeded v1.2.3' },
    { type: 'error', text: 'Error: timeout contacting DB' },
    { type: 'error', text: 'Error: timeout contacting DB' },
    { type: 'error', text: 'Error: timeout contacting DB' },
    { type: 'error', text: 'Sentry: DB connection refused (x5 in 2m)' },
    { type: 'payment', text: 'Stripe: payment failed $499 (ACME VIP)' },
    { type: 'payment', text: 'Stripe: payment failed $499 (ACME VIP)' },
    { type: 'security', text: 'Security: login from new country + new device' },
    { type: 'deploy', text: 'Deploy rollback initiated' },
    { type: 'metric', text: 'CPU 92% sustained' },
    { type: 'error', text: 'Sentry: DB connection refused (x8 in 2m)' },
    { type: 'metric', text: 'System stabilized' },
  ],
}

interface TimelineItem {
  event: EngineResult['event']
  decision: EngineResult['decision']
  executions: EngineResult['executions']
  audit_id: string
}

export default function Stream() {
  const [timeline, setTimeline] = useState<TimelineItem[]>([])
  const [loading, setLoading] = useState(false)
  const [eventType, setEventType] = useState('error')
  const [eventText, setEventText] = useState('')
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const handleSendEvent = async (type: string, text: string) => {
    setLoading(true)
    try {
      const result = await sendEvent({ event_type: type, text })
      setTimeline((prev) => [
        {
          event: result.event,
          decision: result.decision,
          executions: result.executions,
          audit_id: result.audit_id,
        },
        ...prev,
      ])
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = () => {
    if (!eventText.trim()) return
    handleSendEvent(eventType, eventText)
    setEventText('')
  }

  const runScenario = async (events: typeof SCENARIOS.calm) => {
    for (const event of events) {
      await handleSendEvent(event.type, event.text)
      await new Promise((r) => setTimeout(r, 500))
    }
  }

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const getDecisionBadgeVariant = (type: string) => {
    switch (type) {
      case 'IGNORE': return 'ignore'
      case 'DIGEST': return 'digest'
      case 'ACT': return 'act'
      case 'SUPPRESSED': return 'suppressed'
      default: return 'secondary'
    }
  }

  return (
    <div className="container py-8">
      <div className="grid lg:grid-cols-[1fr_300px] gap-8">
        {/* Main content */}
        <div className="space-y-6">
          <div>
            <h1 className="font-display text-3xl">Live Event Stream</h1>
            <p className="text-muted-foreground">Inject events. The agent should ignore most. When it acts, verify delivery.</p>
          </div>

          {/* Tips */}
          <Card className="bg-muted/50">
            <CardContent className="py-3 text-sm">
              <p><strong>Tip:</strong> Start with 3–4 normal events to see IGNORE/DIGEST. Then try "Stripe failed $499" to trigger a real alert. Try repeating the same error 3x to trigger escalation.</p>
            </CardContent>
          </Card>

          {/* Event Composer */}
          <Card>
            <CardHeader>
              <CardTitle>Event Composer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="error">error</SelectItem>
                    <SelectItem value="payment">payment</SelectItem>
                    <SelectItem value="security">security</SelectItem>
                    <SelectItem value="deploy">deploy</SelectItem>
                    <SelectItem value="metric">metric</SelectItem>
                    <SelectItem value="feedback">feedback</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Event text..."
                  value={eventText}
                  onChange={(e) => setEventText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                  className="flex-1"
                />
                <Button onClick={handleSubmit} disabled={loading || !eventText.trim()}>
                  <Play className="h-4 w-4 mr-1" />
                  Submit
                </Button>
              </div>

              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => runScenario(SCENARIOS.calm)} disabled={loading}>
                  <Zap className="h-4 w-4 mr-1" />
                  Auto-run: Calm Day
                </Button>
                <Button variant="outline" size="sm" onClick={() => runScenario(SCENARIOS.incident)} disabled={loading}>
                  <Zap className="h-4 w-4 mr-1" />
                  Auto-run: Incident
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setTimeline([])}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Inject */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Inject</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium mb-2 text-muted-foreground">Noise (should ignore)</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_EVENTS.noise.map((e, i) => (
                    <Button key={i} variant="secondary" size="sm" onClick={() => handleSendEvent(e.type, e.text)} disabled={loading}>
                      {e.text.slice(0, 25)}...
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium mb-2 text-muted-foreground">Potential (might digest)</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_EVENTS.potential.map((e, i) => (
                    <Button key={i} variant="secondary" size="sm" onClick={() => handleSendEvent(e.type, e.text)} disabled={loading}>
                      {e.text.slice(0, 25)}...
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium mb-2 text-accent">Should Trigger</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_EVENTS.trigger.map((e, i) => (
                    <Button key={i} variant="accent" size="sm" onClick={() => handleSendEvent(e.type, e.text)} disabled={loading}>
                      {e.text.slice(0, 25)}...
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <div className="space-y-4">
            <h2 className="font-display text-xl">Timeline</h2>
            {timeline.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No events yet. Use the composer or quick inject buttons above.
                </CardContent>
              </Card>
            ) : (
              timeline.map((item) => (
                <Card key={item.audit_id} className={cn(
                  'transition-all',
                  item.decision.decision_type === 'ACT' && 'border-accent/50'
                )}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={getDecisionBadgeVariant(item.decision.decision_type)}>
                            {item.decision.decision_type}
                          </Badge>
                          <Badge variant="outline">{item.event.event_type}</Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(item.event.ts).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-sm font-medium truncate">{item.event.text}</p>
                        <p className="text-sm text-muted-foreground mt-1">{item.decision.why}</p>
                        {item.decision.incident_key && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Incident: {item.decision.incident_key}
                            {item.decision.suppressed_reason && (
                              <span className="text-yellow-600 ml-2">({item.decision.suppressed_reason})</span>
                            )}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExpand(item.audit_id)}
                      >
                        {expandedItems.has(item.audit_id) ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    {expandedItems.has(item.audit_id) && (
                      <div className="mt-4 space-y-3 border-t pt-4">
                        {item.decision.tool_calls.length > 0 && (
                          <div>
                            <p className="text-xs font-medium mb-1">Tool Calls</p>
                            <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
                              {JSON.stringify(item.decision.tool_calls, null, 2)}
                            </pre>
                          </div>
                        )}
                        {item.executions.length > 0 && (
                          <div>
                            <p className="text-xs font-medium mb-1">Executions</p>
                            {item.executions.map((exec, i) => (
                              <div key={i} className="text-xs bg-muted p-2 rounded mb-1">
                                <div className="flex items-center gap-2">
                                  <Badge variant={exec.ok ? 'success' : 'error'} className="text-[10px]">
                                    {exec.status}
                                  </Badge>
                                  <span className="font-medium">{exec.tool_name}</span>
                                </div>
                                <p className="text-muted-foreground mt-1">{exec.message}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {item.decision.debug && (
                          <div>
                            <p className="text-xs font-medium mb-1">Debug</p>
                            <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-40">
                              {JSON.stringify(item.decision.debug, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Right rail - Actions */}
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Executed Actions</CardTitle>
            </CardHeader>
            <CardContent>
              {timeline.filter((t) => t.executions.length > 0).length === 0 ? (
                <p className="text-sm text-muted-foreground">No actions executed yet.</p>
              ) : (
                <div className="space-y-3">
                  {timeline
                    .filter((t) => t.executions.length > 0)
                    .flatMap((t) =>
                      t.executions.map((exec) => (
                        <div key={`${t.audit_id}-${exec.tool_name}`} className="text-sm border-b pb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant={exec.ok ? 'success' : 'error'} className="text-[10px]">
                              {exec.status}
                            </Badge>
                            <span className="font-medium">{exec.tool_name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {exec.message}
                          </p>
                        </div>
                      ))
                    )}
                </div>
              )}
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  )
}
